import React, { useEffect, useMemo, useState } from 'react'
import { useAuth } from 'context/authentication/useAuth'
import { useQuery } from '@tanstack/react-query'
import { CellContext } from '@tanstack/react-table'
import to from 'await-to-js'
import { DataTable, DataTableProvider } from '../DataTable'
import { IColumnVisibility } from '../DataTable/types'
import { DataTableContainer } from '../DataTable/UI'
import { SamplesApi } from '../../services/samplesApi'
import { SampleRequest } from '../../models/samples'
import { getTableQueryKey } from '../../utils/getTableQueryKey'
import { dataTableSortingStoreRepo } from '../DataTable/DataTableSorting/DataTableSortingStore'
import { ActionsDropdown } from '../Buttons/ActionsDropdown'
import { toast } from 'react-toastify'
import { FbLink } from '../FbUI/FbLink'
import { TextPill } from '../FbUI/TextPill'
import FbButton from '../FbUI/FbButton'
import { format } from 'date-fns'
import { formatDateOnly } from '../../utils/formatting'
import { ColumnsStoreProvider } from '../../stores/ColumnsStore/ColumnsStoreProvider'
import { filterStoreRepo, useFilterParams } from '../Filters/FilterStore'
import { FilterIdentifier } from '../../models/saved_view'
import { TableSearch } from '../Filters/TableSearch'
import { FilterChips } from '../Filters/components/FilterChips/FilterChips'
import * as S from '../Tables/CommonTable.styles'
import { SamplesFilterset } from './SamplesFilterset'
import { ColumnSelectorRecipient } from '../Modals/ColumnModal/ColumnModal'
import { SampleRequestPreviewModal } from './SampleRequestPreviewModal'
import SampleRequestExportModal from './SampleRequestExportModal'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { BsDownload } from 'react-icons/bs'
import styled from 'styled-components'
import { exportSampleRequests } from '../../services/samplesApi'
import { CancelSampleRequestModal } from './CancelSampleRequestModal'
import { SampleRequestReviewModal } from './SampleRequestReviewModal'
import { SampleStatusBadge } from './SampleStatusBadge'
import { ClipboardCheck, Trash2 } from 'lucide-react'
import { cn } from '../UI/cn'
import { PillListCell } from '../Tables/CommonTableCells/PillListCell'

const PAGE_SIZE = 50

const tooltip = <Tooltip id="tooltip">Download limited to 10,000 rows</Tooltip>

interface SamplesTableProps {
  setTotalRowsCount?: (count: number) => void
  onEditRequest?: (sampleId: number) => void
  onPreviewRequest?: (sampleId: number) => void
  tableKey: string
  myRequestsOnly?: boolean
  statusFilter?: string
}

function SamplesTableComponent({
  setTotalRowsCount,
  onPreviewRequest,
  myRequestsOnly,
  statusFilter,
  tableKey,
}: SamplesTableProps) {
  const { isLimited } = useAuth()
  // Use different filter identifier for each table to avoid filter sharing
  const IDENTIFIER = (
    statusFilter
      ? 'NEEDS_REVIEW_SAMPLES_TABLE'
      : myRequestsOnly
        ? 'MY_SAMPLES_TABLE'
        : 'SAMPLES_TABLE'
  ) as FilterIdentifier
  const samplesFilterStore = filterStoreRepo.getStore(IDENTIFIER)
  const { page, setPage } = samplesFilterStore()
  const filterParams = useFilterParams(samplesFilterStore)
  const sortingStore = dataTableSortingStoreRepo.getStore(tableKey)
  const { sorting, setSorting } = sortingStore()
  const [previewRequestId, setPreviewRequestId] = useState<number | null>(null)
  const [showExportModal, setShowExportModal] = useState(false)
  const [showInactive, setShowInactive] = useState(false)
  const controllerRef = React.useRef<AbortController>(new AbortController())

  // Set default sort to created (input date) descending on initial load
  useEffect(() => {
    if (!sorting || sorting.length === 0) {
      setSorting([{ id: 'created', desc: true }])
    }
  }, [])

  const handleExport = async (
    selectedColumns: string[],
    exportType: 'csv' | 'xlsx'
  ) => {
    await exportSampleRequests(
      selectedColumns,
      exportType,
      filterAndSortParams,
      controllerRef.current
    )
  }

  React.useEffect(() => {
    return () => {
      controllerRef.current.abort()
    }
  }, [])
  const [cancelModalOpen, setCancelModalOpen] = useState(false)
  const [cancelRequestData, setCancelRequestData] = useState<{
    id: number
    requestNumber: string
  } | null>(null)
  const [isCancelling, setIsCancelling] = useState(false)
  const [reviewRequestId, setReviewRequestId] = useState<number | null>(null)

  const sortParams = useMemo(() => {
    const params: Record<string, unknown> = {}
    if (sorting?.length) {
      params['ordering'] = sorting[0].desc
        ? '-' + sorting[0]?.id
        : sorting[0]?.id
    }
    return params
  }, [sorting])

  const filterAndSortParams = useMemo(() => {
    const params = {
      ...filterParams,
      ...sortParams,
    }

    // Add status exclusion when showInactive is false
    // BUT only if there's no explicit status filter already set
    const hasStatusFilter =
      filterParams.status &&
      (Array.isArray(filterParams.status)
        ? filterParams.status.length > 0
        : true)
    if (!showInactive && !hasStatusFilter) {
      params['status__exclude'] = ['cancelled']
    }

    return params
  }, [filterParams, sortParams, showInactive])

  const TABLE_QUERY_KEY = getTableQueryKey({
    tableKey,
    filterParams: filterAndSortParams,
    page,
  })

  const { isFetching, data, refetch } = useQuery({
    queryKey: TABLE_QUERY_KEY,
    queryFn: async () => {
      return await SamplesApi.listSampleRequests({
        ...filterAndSortParams,
        page,
        page_size: PAGE_SIZE,
        my_requests_only: myRequestsOnly,
        ...(statusFilter ? { status: statusFilter as any } : {}),
      })
    },
  })

  useEffect(() => {
    setTotalRowsCount?.(data?.count ?? 0)
  }, [data?.count])

  const formatDateTime = (dateString: string) => {
    try {
      const date = new Date(dateString)
      return {
        date: format(date, 'MM/dd/yyyy'),
        time: format(date, 'h:mm a'),
      }
    } catch {
      return { date: dateString, time: '' }
    }
  }

  const handleCancelClick = (sampleId: number, requestNumber: string) => {
    setCancelRequestData({ id: sampleId, requestNumber })
    setCancelModalOpen(true)
  }

  const handleCancelConfirm = async (reason: string) => {
    if (!cancelRequestData) return

    setIsCancelling(true)
    const [err] = await to(
      SamplesApi.cancelSampleRequest(cancelRequestData.id, reason)
    )

    setIsCancelling(false)

    if (!err) {
      toast.success('Sample request cancelled successfully')
      setCancelModalOpen(false)
      setCancelRequestData(null)
      void refetch()
    } else {
      toast.error('Failed to cancel sample request')
    }
  }

  const columns = useMemo(
    () => [
      {
        id: 'status',
        header: 'Status',
        accessorKey: 'status_display',
        cell: ({ row }: CellContext<SampleRequest, unknown>) => (
          <SampleStatusBadge
            status={row.original.status}
            label={row.original.status_display}
            style={{ marginBottom: 2 }}
          />
        ),
        size: 130,
        meta: {
          headerGroup: 'Order Details',
        },
      },
      {
        id: 'fulfillment_option_display',
        header: 'Fulfillment Option',
        accessorKey: 'fulfillment_option_display',
        size: 180,
        meta: {
          headerGroup: 'Order Details',
        },
      },
      {
        id: 'request_number',
        header: 'Request #',
        accessorKey: 'request_number',
        cell: ({ row }: CellContext<SampleRequest, unknown>) => {
          const isCancelled = row.original.status === 'cancelled'
          return (
            <div className={cn(isCancelled && 'opacity-60')}>
              <FbLink
                to={`?sample_request_id=${row.original.id}`}
                onClick={(e) => {
                  e.preventDefault()
                  if (onPreviewRequest) {
                    onPreviewRequest(row.original.id)
                  } else {
                    setPreviewRequestId(row.original.id)
                  }
                }}
                style={{ cursor: 'pointer', fontWeight: 'bold' }}
              >
                {row.original.request_number}
              </FbLink>
            </div>
          )
        },
        size: 120,
        meta: {
          headerGroup: 'Order Details',
          isSticky: true,
          isFirstColumn: true,
          isEditable: false,
        },
      },
      {
        id: 'created',
        header: 'Created',
        accessorKey: 'created',
        cell: ({ getValue, row }: CellContext<SampleRequest, unknown>) => {
          const isCancelled = row.original.status === 'cancelled'
          const { date, time } = formatDateTime(getValue() as string)
          return (
            <div className={cn(isCancelled && 'opacity-60')}>
              <div>{date}</div>
              <div className="text-xs text-gray-500">{time}</div>
            </div>
          )
        },
        size: 120,
        meta: {
          headerGroup: 'Order Details',
        },
      },
      {
        id: 'date_needed',
        header: 'Need by',
        accessorKey: 'date_needed',
        cell: ({ getValue, row }: CellContext<SampleRequest, unknown>) => {
          const isCancelled = row.original.status === 'cancelled'
          return (
            <div className={cn(isCancelled && 'opacity-60')}>
              {formatDateOnly(getValue() as string)}
            </div>
          )
        },
        size: 120,
        meta: {
          headerGroup: 'Order Details',
        },
      },
      {
        id: 'user_name',
        header: 'Requestor',
        accessorKey: 'user_name',
        cell: ({ getValue, row }: CellContext<SampleRequest, unknown>) => {
          const isCancelled = row.original.status === 'cancelled'
          return (
            <div className={cn(isCancelled && 'opacity-60')}>
              <TextPill
                backgroundColor="#E5E7EB"
                textColor="#374151"
                style={{ marginBottom: 2 }}
              >
                {getValue() as string}
              </TextPill>
            </div>
          )
        },
        size: 150,
        meta: {
          headerGroup: 'Requestor Info',
        },
      },
      {
        id: 'user_email',
        header: 'Requestor Email',
        accessorKey: 'user_email',
        cell: ({ getValue, row }: CellContext<SampleRequest, unknown>) => {
          const isCancelled = row.original.status === 'cancelled'
          return (
            <div className={cn(isCancelled && 'opacity-60')}>
              {getValue() as string}
            </div>
          )
        },
        size: 200,
        meta: {
          headerGroup: 'Requestor Info',
        },
      },
      {
        id: 'company_name',
        header: 'Company',
        accessorKey: 'company_name',
        size: 250,
        cell: ({ row }: CellContext<SampleRequest, unknown>) => {
          if (
            !row.original.company_id ||
            !row.original.company_notification_link
          ) {
            return row.original.company_name || '-'
          }
          return (
            <FbLink to={row.original.company_notification_link}>
              {row.original.company_name}
            </FbLink>
          )
        },
        meta: {
          headerGroup: 'Requestor Info',
        },
      },
      {
        id: 'location_name',
        header: 'Location',
        accessorKey: 'location_name',
        size: 250,
        cell: ({ row }: CellContext<SampleRequest, unknown>) => {
          if (
            !row.original.location_id ||
            !row.original.location_notification_link
          ) {
            return '-'
          }
          return (
            <FbLink to={row.original.location_notification_link}>
              {row.original.location_name}
            </FbLink>
          )
        },
        meta: {
          headerGroup: 'Order Details',
        },
      },
      {
        id: 'line_items',
        header: 'Products',
        accessorKey: 'line_items',
        cell: ({ row }: CellContext<SampleRequest, unknown>) => {
          const items = row.original.line_items || []
          const isCancelled = row.original.status === 'cancelled'
          if (items.length === 0)
            return <div className={cn(isCancelled && 'opacity-60')}>-</div>

          // Format items as strings for PillListCell
          const productNames = items.map(
            (item) => `${item.product_name} - ${item.variant_name}`
          )

          return (
            <div className={cn(isCancelled && 'opacity-60')}>
              <PillListCell
                items={productNames}
                maxColumnWidth={400}
                maxPillWidth={180}
              />
            </div>
          )
        },
        size: 400,
        meta: {
          headerGroup: 'Order Details',
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        size: 80,
        meta: {
          isEditable: false,
          headerDisableOrdering: true,
          rightAlign: true,
          isLastColumn: true, // Always last but NOT sticky (preventLastColumnSticky prop disables sticky)
        },
        cell: ({ row }: CellContext<SampleRequest, unknown>) => {
          const sample = row.original
          const canCancel = sample.status === 'submitted'
          const canReview = sample.status === 'pending_review' && !isLimited

          const actions = [
            ...(canReview
              ? [
                  {
                    label: 'Review',
                    icon: ClipboardCheck,
                    onClick: () => setReviewRequestId(sample.id),
                  },
                ]
              : []),
            {
              label: 'Delete',
              icon: Trash2,
              onClick: () =>
                handleCancelClick(sample.id, sample.request_number),
              disabled: !canCancel,
              variant: 'destructive' as const,
            },
          ]

          return <ActionsDropdown actions={actions} className="justify-end" />
        },
      },
    ],
    [onPreviewRequest, isLimited]
  )

  return (
    <>
      <DataTableContainer>
        <DataTable
          tableHeader={
            <S.SearchContainer>
              <div className="flex w-full gap-2 items-center">
                <TableSearch
                  className={'flex-1'}
                  filterStore={samplesFilterStore}
                  searchPlaceholder="Search by request number or company..."
                  tableKey={tableKey}
                />
                <SamplesFilterset filterIdentifier={IDENTIFIER} />
                <ColumnSelectorRecipient tableKey={tableKey} />
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showInactive}
                    onChange={(e) => setShowInactive(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 accent-fb-green-800 focus:ring-2 focus:ring-fb-green-800"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Show Inactive
                  </span>
                </label>
              </div>
              <FilterChips
                identifier={IDENTIFIER}
                store={samplesFilterStore}
                clearAllButton
                showActive
              />
            </S.SearchContainer>
          }
          loading={isFetching}
          tableKey={tableKey}
          data={data?.results ?? []}
          columns={columns as any}
          sortableFields={[
            'request_number',
            'created',
            'date_needed',
            'user_name',
            'user_email',
            'company_name',
            'location_name', // Added for FIR-4061
            'status',
          ]}
          virtualizeRows
          isPaginationEnabled
          preventLastColumnSticky
          paginationOptions={{
            totalRows: data?.count ?? 0,
            pageSize: PAGE_SIZE,
            setPage,
            page,
            isPaginationLoading: isFetching,
          }}
          emptyTableText={{
            title: 'No sample requests found',
            subtitle:
              'Try adjusting your filters or create a new sample request',
          }}
          footerControls={
            <OverlayTrigger placement="top" overlay={tooltip}>
              <ExportButton
                variant="secondary"
                onClick={() => setShowExportModal(true)}
              >
                <BsDownload size={18} />
              </ExportButton>
            </OverlayTrigger>
          }
        />
      </DataTableContainer>

      {previewRequestId && (
        <SampleRequestPreviewModal
          show={!!previewRequestId}
          onHide={() => setPreviewRequestId(null)}
          sampleRequestId={previewRequestId}
        />
      )}

      {showExportModal && (
        <SampleRequestExportModal
          show={showExportModal}
          onSubmit={handleExport}
          handleClose={() => setShowExportModal(false)}
        />
      )}
      <CancelSampleRequestModal
        open={cancelModalOpen}
        onOpenChange={setCancelModalOpen}
        onConfirm={handleCancelConfirm}
        isSubmitting={isCancelling}
        requestNumber={cancelRequestData?.requestNumber}
      />

      <SampleRequestReviewModal
        show={!!reviewRequestId}
        onHide={() => setReviewRequestId(null)}
        sampleRequestId={reviewRequestId}
        onSuccess={() => void refetch()}
      />
    </>
  )
}

export function SamplesTable(
  props: SamplesTableProps & {
    defaultColumnVisibility?: IColumnVisibility<any>
  }
) {
  const { tableKey, defaultColumnVisibility, ...rest } = props

  // Define default column visibility and order (FIR-4061: user_name leftmost, status rightmost)
  const defaultVisibility = defaultColumnVisibility || {
    user_name: true,
    company_name: true,
    location_name: false, // Hidden by default per FIR-4061 feedback
    request_number: true,
    created: true,
    date_needed: true,
    user_email: false, // Hidden by default per FIR-4061 feedback
    line_items: true,
    status: true,
    fulfillment_option_display: false, // Hidden by default per FIR-4061 feedback
    actions: true,
  }

  const initialColumnOrder = [
    'request_number', // Leftmost and sticky
    'company_name',
    'location_name',
    'user_name', // Requestor/Operator Name
    'created',
    'date_needed',
    'user_email',
    'line_items',
    'status',
    'fulfillment_option_display',
    'actions', // Always last (not sticky)
  ]

  const preferredGroupOrder = ['Order Details', 'Requestor Info', 'Actions']

  return (
    <ColumnsStoreProvider
      tableKey={tableKey}
      savedPresetsIdentifier={'SAMPLES_TABLE' as FilterIdentifier}
      defaultColumnVisibility={defaultVisibility}
      initialColumnOrder={initialColumnOrder}
      preferredGroupOrder={preferredGroupOrder}
    >
      <DataTableProvider tableKey={tableKey}>
        <SamplesTableComponent tableKey={tableKey} {...rest} />
      </DataTableProvider>
    </ColumnsStoreProvider>
  )
}

const ExportButton = styled(FbButton)`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px 14px;

  gap: 8px;

  height: 44px;

  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
`
